/* eslint global-require: "off" */

/**
 * @module comparePlans/env
 *
 * The environment variables manager. This module helps to work with the
 * variations between development, production and test by providing a common
 * import source that auto-magically has the relevant environment-specific
 * variables.
 *
 * The exported interface (the `env` object) is filled with the key-value tuples
 * exported by the relevant file in `src/env/${development|test|production}.js`.
 */

/**
 * This JSDoc type annotation is here just so that the @module declaration above
 * doesn't implicitly apply to this `const` below.
 *
 * @type {Object}
 */
const env = {};

/**
 * This method will actually fill our `env` object with the environment vars
 * that were imported above. It will also make `env` immutable, so this file is
 * the only one that can determine its contents.
 */
export function seed(seedData) {
  /**
   * Copy all seedData key-value pairs into our `env` object.
   */
  Object.assign(env, seedData);
} /* we can't test non-test paths in the test env! */

/**
 * WARNING: LEAKING ABSTRACTION AHEAD
 *
 * In an ideal world, this script would not be aware of any build system or
 * node process or even the CommonJS `require()` method. In this hypothetical
 * ideal world, this file would be a pure ES6 module, and the `seed` method
 * could be called by some higher-up file whose responsibility is to be the
 * single point of multi-module-format blurred lines, and be Webpack-aware.
 *
 * Unfortunately, the execution order of the files in our project is lowest-to-
 * highest, i.e.: the top-level scripts execute last and scripts and modules
 * lower down in the dependency tree for this app execute first. The result of
 * this means that any services or scripts that expect the `env` object to be
 * ready will actually fail because if we seed the `env` object with its
 * expected data at the top-level script, it won't be ready in time for when the
 * lower-level scripts want to read data from it.
 *
 * All of this together means that we need to make this module aware of the
 * build process and seed its data at compile time, so by the time the JS
 * executes and exports the `env` at the bottom of this file, it will have been
 * `seed()`-ed with the data already.
 *
 * Fortunately, the extent of the leaky abstraction ends here, and the rest of
 * the app can use this module as a dependency, assuming that it is a simple
 * object of hard-coded key-value pairs as if this mess never needed to happen.
 */
/* istanbul ignore next */ if (process.env.NODE_ENV === 'production') {
  seed(require('../env/production.js'));

  /**
   * WHen compiled in Origin-CMS, the webpack build is invoked as a production
   * build. So, when loaded in a developer's local AEM environment, the ENV
   * was coming out as `production`, when it should have been `development`.
   * To fix this, we do a final check at runtime based on the browser's URL
   * to see if it's one of the URLs that developers use while developing
   * locally, and override the ENV data to `development` when so.
   *
   * @todo: somehow don't `require` in the dev env data when in production
   */
  const developmentData = require('../env/development.js');
  if (getEnvNameFromUrl() === 'development') {
    seed(developmentData);
  }
} else if (process.env.NODE_ENV === 'test') {
  seed(require('../env/test.js'));
} else {
  /* istanbul ignore next */
  seed(require('../env/development.js'));
}

/**
 * @returns {string}
 */
function getEnvNameFromUrl() {
  const host = global.location.hostname;

  if (host.includes('localhost')) {
    return 'development';
  }

  if (host.includes('origin-local')) {
    return 'development';
  }

  return 'production';
}

/**
 * Then disallow any further editing of the object from here on out.
 */
Object.freeze(env);

/**
 * This object is available for import by any level of the application
 */
export default env;
